import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { renderMarkdown } from '../lib/renderMarkdown';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  ArrowLeft,
  Bot,
  TrendingUp,
  Users,
  Loader2,
  Download,
  DollarSign,
  ShoppingCart,
  BookOpen
} from 'lucide-react';
import ReglementationTab from './ReglementationTab';

// --- TYPES ---
interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

// --- SUGGESTIONS RAPIDES ---
const QUICK_ACTIONS = [
  {
    id: 'rentability',
    label: 'Audit de Rentabilité',
    description: 'Identifie les dépassements de budget et les projets critiques.',
    icon: TrendingUp,
    color: '#3b82f6',
    bg: '#eff6ff',
    message: 'Fais-moi un audit complet de la rentabilité des chantiers. Quels sont les projets à risque et les dépassements de budget ?'
  },
  {
    id: 'hr',
    label: 'Climat Social & RH',
    description: 'Absences en cours, disponibilité des équipes.',
    icon: Users,
    color: '#ef4444',
    bg: '#fef2f2',
    message: 'Fais-moi un point sur les absences actuelles et à venir. Quelles équipes sont impactées ?'
  },
  {
    id: 'finance',
    label: 'Synthèse Financière',
    description: 'Chiffre d\'affaires, marges, vision globale.',
    icon: DollarSign,
    color: '#059669',
    bg: '#ecfdf5',
    message: 'Donne-moi une synthèse financière globale : chiffre d\'affaires total, marges par chantier, et les points d\'attention.'
  },
  {
    id: 'achats',
    label: 'Bilan Achats',
    description: 'Top fournisseurs, volumes, tendances.',
    icon: ShoppingCart,
    color: '#d97706',
    bg: '#fffbeb',
    message: 'Fais-moi un bilan des achats récents : volumes, top fournisseurs, et y a-t-il des anomalies ou des optimisations possibles ?'
  },
];

export default function AdminAssistant() {
  const navigate = useNavigate();
  const chatEndRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState<'assistant' | 'reglementation'>('assistant');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);

  // Auto-scroll quand nouveaux messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // --- APPEL EDGE FUNCTION ---
  const callAssistant = async (message: string): Promise<string> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Non authentifié');

    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/assistant-chat`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ message })
      }
    );

    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Erreur Edge Function');
    return result.response;
  };

  // --- ENVOYER UN MESSAGE ---
  const handleSend = async (text: string) => {
    const msg = text.trim();
    if (!msg || loading) return;

    // Ajouter le message utilisateur
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: msg,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const response = await callAssistant(msg);
      const assistantMsg: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (error) {
      const errorMsg: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `**Erreur** : ${error instanceof Error ? error.message : 'Impossible de contacter l\'assistant. Vérifiez votre connexion.'}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  // --- EXPORT PDF ---
  const exportReportPDF = (question: string, responseText: string) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    const contentWidth = pageWidth - margin * 2;

    // En-tête
    doc.setFontSize(18);
    doc.setTextColor(5, 150, 105);
    doc.text('CENTRALIS', margin, 20);
    doc.setFontSize(11);
    doc.setTextColor(100, 116, 139);
    doc.text('Rapport de Direction', 60, 20);
    doc.setFontSize(9);
    doc.text(`${new Date().toLocaleDateString('fr-FR')} - ${new Date().toLocaleTimeString('fr-FR')}`, margin, 28);
    doc.setDrawColor(5, 150, 105);
    doc.setLineWidth(0.5);
    doc.line(margin, 32, pageWidth - margin, 32);

    // Question
    doc.setFontSize(11);
    doc.setTextColor(30, 41, 59);
    doc.setFont('helvetica', 'bold');
    const qLines = doc.splitTextToSize(`Question : ${question}`, contentWidth);
    doc.text(qLines, margin, 40);

    const qHeight = qLines.length * 5 + 40;
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, qHeight + 2, pageWidth - margin, qHeight + 2);

    // --- PARSING BLOC PAR BLOC ---
    let yPos = qHeight + 8;
    const lines = responseText.split('\n');
    let idx = 0;

    const checkPage = (needed: number) => {
      if (yPos + needed > 275) {
        doc.addPage();
        yPos = 20;
      }
    };

    while (idx < lines.length) {
      const line = lines[idx];

      // --- TABLEAU MARKDOWN → autoTable ---
      if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
        const tableLines: string[] = [];
        while (idx < lines.length && lines[idx].trim().startsWith('|') && lines[idx].trim().endsWith('|')) {
          tableLines.push(lines[idx].trim());
          idx++;
        }
        if (tableLines.length >= 2) {
          const parseCells = (row: string) => row.split('|').filter(c => c.trim() !== '').map(c => c.trim().replace(/\*\*/g, ''));
          const headerRow = parseCells(tableLines[0]);
          const isSeparator = tableLines[1].trim().replace(/[|\-:\s]/g, '') === '';
          const dataStartIdx = isSeparator ? 2 : 1;
          const bodyRows = tableLines.slice(dataStartIdx).map(parseCells);

          checkPage(20 + bodyRows.length * 8);

          autoTable(doc, {
            startY: yPos,
            head: isSeparator ? [headerRow] : undefined,
            body: isSeparator ? bodyRows : [headerRow, ...bodyRows],
            margin: { left: margin, right: margin },
            styles: { fontSize: 8, cellPadding: 3, textColor: [51, 65, 85], lineColor: [226, 232, 240], lineWidth: 0.3 },
            headStyles: { fillColor: [248, 250, 252], textColor: [30, 41, 59], fontStyle: 'bold', lineWidth: 0.3 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            theme: 'grid'
          });

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          yPos = (doc as any).lastAutoTable.finalY + 6;
          continue;
        }
      }

      // --- TITRES ---
      const titleMatch = line.match(/^(#{1,3})\s+(.+)$/);
      if (titleMatch) {
        const level = titleMatch[1].length;
        const titleText = titleMatch[2].replace(/\*\*/g, '');
        const fontSize = level === 1 ? 14 : level === 2 ? 12 : 11;
        checkPage(12);
        yPos += 4;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(fontSize);
        doc.setTextColor(30, 41, 59);
        doc.text(titleText, margin, yPos);
        yPos += fontSize === 14 ? 8 : 6;
        if (level <= 2) {
          doc.setDrawColor(241, 245, 249);
          doc.setLineWidth(0.3);
          doc.line(margin, yPos - 2, pageWidth - margin, yPos - 2);
        }
        doc.setFont('helvetica', 'normal');
        idx++;
        continue;
      }

      // --- SÉPARATEURS ---
      if (/^-{3,}$/.test(line.trim()) || /^\*{3,}$/.test(line.trim())) {
        checkPage(8);
        yPos += 2;
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.3);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 6;
        idx++;
        continue;
      }

      // --- LISTES À PUCES ---
      if (/^[\-\*]\s/.test(line.trim())) {
        const itemText = line.trim().replace(/^[\-\*]\s+/, '').replace(/\*\*/g, '');
        checkPage(6);
        doc.setFontSize(10);
        doc.setTextColor(51, 65, 85);
        const wrapped = doc.splitTextToSize(itemText, contentWidth - 8);
        doc.text('\u2022', margin + 2, yPos);
        doc.text(wrapped, margin + 8, yPos);
        yPos += wrapped.length * 5;
        idx++;
        continue;
      }

      // --- LISTES NUMÉROTÉES ---
      const numMatch = line.trim().match(/^(\d+)[\.\)]\s+(.+)/);
      if (numMatch) {
        const num = numMatch[1];
        const itemText = numMatch[2].replace(/\*\*/g, '');
        checkPage(6);
        doc.setFontSize(10);
        doc.setTextColor(51, 65, 85);
        const wrapped = doc.splitTextToSize(itemText, contentWidth - 10);
        doc.text(`${num}.`, margin + 1, yPos);
        doc.text(wrapped, margin + 10, yPos);
        yPos += wrapped.length * 5;
        idx++;
        continue;
      }

      // --- LIGNE VIDE ---
      if (line.trim() === '') {
        yPos += 3;
        idx++;
        continue;
      }

      // --- PARAGRAPHE NORMAL ---
      const cleanLine = line.replace(/\*\*/g, '');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(51, 65, 85);
      const wrapped = doc.splitTextToSize(cleanLine, contentWidth);
      checkPage(wrapped.length * 5);
      doc.text(wrapped, margin, yPos);
      yPos += wrapped.length * 5;
      idx++;
    }

    // Footer sur chaque page
    const pageCount = doc.getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text('Oger Energies - Centralis', margin, 290);
      doc.text(`Page ${p}/${pageCount}`, 180, 290);
    }

    doc.save(`Rapport_Centralis_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const hasMessages = messages.length > 0;

  return (
    <div style={{
      fontFamily: 'Inter, sans-serif',
      maxWidth: '1000px',
      margin: '0 auto',
      paddingBottom: '40px',
      display: 'flex',
      flexDirection: 'column',
      height: activeTab === 'assistant' && hasMessages ? 'calc(100vh - 80px)' : 'auto'
    }}>

      {/* HEADER */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '24px', flexShrink: 0
      }}>
        <button
          onClick={() => {
            if (hasMessages) {
              setMessages([]);
            } else {
              navigate('/');
            }
          }}
          style={{
            background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px',
            padding: '10px', cursor: 'pointer'
          }}
        >
          <ArrowLeft size={20} color="#64748b" />
        </button>
        <div>
          <h1 style={{
            margin: 0, fontSize: '1.5rem', color: '#1e293b',
            display: 'flex', alignItems: 'center', gap: '10px'
          }}>
            Assistant de Direction
          </h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.9rem' }}>
            {activeTab === 'assistant'
              ? 'Synthèses opérationnelles et financières'
              : 'Fiches réglementaires et normes applicables'}
          </p>
        </div>
      </div>

      {/* TAB BAR */}
      <div style={{
        display: 'flex', gap: '4px', marginBottom: '20px',
        background: '#f1f5f9', borderRadius: '12px', padding: '4px'
      }}>
        <button
          onClick={() => setActiveTab('assistant')}
          style={{
            flex: 1, padding: '10px 20px', borderRadius: '10px', border: 'none',
            cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            background: activeTab === 'assistant' ? 'white' : 'transparent',
            color: activeTab === 'assistant' ? '#1e293b' : '#64748b',
            boxShadow: activeTab === 'assistant' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.2s'
          }}
        >
          <Bot size={16} /> Assistant IA
        </button>
        <button
          onClick={() => setActiveTab('reglementation')}
          style={{
            flex: 1, padding: '10px 20px', borderRadius: '10px', border: 'none',
            cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            background: activeTab === 'reglementation' ? 'white' : 'transparent',
            color: activeTab === 'reglementation' ? '#1e293b' : '#64748b',
            boxShadow: activeTab === 'reglementation' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.2s'
          }}
        >
          <BookOpen size={16} /> Réglementation
        </button>
      </div>

      {/* ONGLET ASSISTANT IA */}
      {activeTab === 'assistant' && <>

      {/* SUGGESTIONS RAPIDES (visible si pas encore de messages) */}
      {!hasMessages && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px'
          }}>
            {QUICK_ACTIONS.map(action => {
              const Icon = action.icon;
              return (
                <div
                  key={action.id}
                  onClick={() => handleSend(action.message)}
                  style={{
                    background: 'white', padding: '20px', borderRadius: '16px',
                    border: '1px solid #e2e8f0', cursor: 'pointer',
                    transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = action.color;
                    e.currentTarget.style.boxShadow = `0 4px 12px ${action.color}20`;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)';
                  }}
                >
                  <div style={{
                    background: action.bg, width: 'fit-content', padding: '10px',
                    borderRadius: '10px', color: action.color, marginBottom: '12px'
                  }}>
                    <Icon size={22} />
                  </div>
                  <h3 style={{ margin: '0 0 5px', color: '#1e293b', fontSize: '1rem' }}>
                    {action.label}
                  </h3>
                  <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>
                    {action.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ZONE DE CONVERSATION */}
      {hasMessages && (
        <div style={{
          flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column',
          gap: '16px', paddingBottom: '16px', marginBottom: '8px'
        }}>
          {messages.map(msg => (
            <div
              key={msg.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                gap: '6px'
              }}
            >
              {/* Bulle message */}
              <div style={{
                maxWidth: msg.role === 'user' ? '70%' : '90%',
                background: msg.role === 'user' ? '#059669' : 'white',
                color: msg.role === 'user' ? 'white' : '#334155',
                borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                padding: msg.role === 'user' ? '12px 18px' : '20px 24px',
                border: msg.role === 'user' ? 'none' : '1px solid #e2e8f0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                fontSize: '0.95rem',
                lineHeight: '1.7'
              }}>
                {msg.role === 'user' ? (
                  <span>{msg.content}</span>
                ) : (
                  <div
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                  />
                )}
              </div>

              {/* Actions sous le message assistant */}
              {msg.role === 'assistant' && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    onClick={() => {
                      // Trouver la question correspondante (message précédent)
                      const idx = messages.findIndex(m => m.id === msg.id);
                      const questionMsg = idx > 0 ? messages[idx - 1] : null;
                      exportReportPDF(
                        questionMsg?.content || 'Analyse',
                        msg.content
                      );
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '6px 14px', background: '#f8fafc', border: '1px solid #e2e8f0',
                      borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem',
                      color: '#475569', fontWeight: '600', transition: 'all 0.15s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#ecfdf5'; e.currentTarget.style.borderColor = '#059669'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                  >
                    <Download size={13} /> Exporter PDF
                  </button>
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                    {msg.timestamp.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    {' · Centralis'}
                  </span>
                </div>
              )}
            </div>
          ))}

          {/* Indicateur de chargement */}
          {loading && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              background: 'white', border: '1px solid #e2e8f0',
              borderRadius: '16px 16px 16px 4px', padding: '16px 20px',
              maxWidth: '300px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <Loader2 size={18} color="#059669" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ color: '#64748b', fontSize: '0.9rem' }}>Analyse en cours...</span>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>
      )}

      {/* PLACEHOLDER INITIAL */}
      {!hasMessages && (
        <div style={{
          textAlign: 'center', color: '#94a3b8', padding: '40px 0 20px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px'
        }}>
          <Bot size={40} strokeWidth={1.2} />
          <p style={{ margin: 0, fontSize: '0.95rem' }}>
            Sélectionnez une analyse ci-dessus
          </p>
        </div>
      )}

      </>}

      {/* ONGLET RÉGLEMENTATION */}
      {activeTab === 'reglementation' && <ReglementationTab />}

      {/* CSS pour animation spin */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
